import { ClockIcon } from '@phosphor-icons/react'
import { useEffect, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { SelectFormField } from '@/components/form/select-form-field'
import { DatePicker } from '@/components/ui/date-picker'
import { DatePickerInput } from '@/components/ui/date-picker-input'

import { getPermissionTypeOptions, getWeekdays, TIME_OPTIONS } from '../add-visitor-modal.constants'
import * as S from '../add-visitor-modal.styled'
import {
  AddVisitorFormValues,
  PermissionTypeEnum,
  VisitorTypeEnum,
} from '../add-visitor-modal.types'
import { calculateMonthlyPattern } from '../add-visitor-modal.utils'
import * as SS from './permission-section.styled'

export const PermissionSection = () => {
  const { t } = useTranslation(['visitors'])
  const { setValue } = useFormContext<AddVisitorFormValues>()
  const PERMISSION_TYPE_OPTIONS = useMemo(() => getPermissionTypeOptions(t), [t])
  const WEEKDAYS = useMemo(() => getWeekdays(t), [t])

  const permissionType = useWatch({
    name: 'permissionType',
  })
  const dueDate = useWatch({ name: 'dueDate' })
  const startOfDay = useWatch({ name: 'startOfDay' })
  const endOfDay = useWatch({ name: 'endOfDay' })
  const selectedDays = (useWatch({ name: 'selectedDays' }) as number[] | undefined) ?? []
  const monthlyPermission = useWatch({ name: 'monthlyPermission' })
  const monthlyRange = useWatch({ name: 'monthlyRange' }) ?? [null, null]
  const keyAccess = useWatch({ name: 'keyAccess' })
  const pinCode = useWatch({ name: 'pinCode' })
  const visitorType = useWatch({ name: 'visitorType' })
  const isVendor = visitorType === VisitorTypeEnum.enum.vendor

  // Note: Keyless Access and PIN code access require an explicit, bounded visit window, so
  // they're only available with the One Time (custom) permission type.
  // Note: As it was described in EL-14633 - keylessAccess is temporarly treated as physical key access,
  // and keyAccess is treated as keyless integration
  const isKeyIntegrationEnabled = !!keyAccess || !!pinCode

  // Note: While locked to the "One Time" permission type by a key integration, the dropdown
  // shows a distinct "Custom" label/option instead of reusing the regular "One Time" entry -
  // "Custom" only ever appears in this locked state, never as a normal selectable option.
  const permissionTypeOptions = isKeyIntegrationEnabled
    ? [{ label: t('visitors:permissions.custom'), value: PermissionTypeEnum.enum.custom }]
    : PERMISSION_TYPE_OPTIONS

  useEffect(() => {
    if (isKeyIntegrationEnabled && permissionType !== PermissionTypeEnum.enum.custom) {
      setValue('permissionType', PermissionTypeEnum.enum.custom, { shouldValidate: true })
    }
  }, [isKeyIntegrationEnabled, permissionType, setValue])

  const handleDayToggle = (dayIndex: number) => {
    const next = selectedDays.includes(dayIndex)
      ? selectedDays.filter((d: number) => d !== dayIndex)
      : [...selectedDays, dayIndex]
    setValue('selectedDays', next, { shouldValidate: true })
  }

  const minDate = new Date()

  return (
    <SS.Root>
      <SS.PermissionSelectWrapper>
        <SelectFormField<AddVisitorFormValues>
          name="permissionType"
          label={t('visitors:form.permission')}
          placeholder={t('visitors:dashboard.permissionSection.selectPermissionTypePlaceholder')}
          options={permissionTypeOptions}
          disabled={isKeyIntegrationEnabled}
        />
      </SS.PermissionSelectWrapper>

      {permissionType === PermissionTypeEnum.enum.always && !isVendor && (
        <SS.FullWidthAlert
          type="info"
          message={t('visitors:dashboard.permissionSection.alwaysKeylessPinAlert')}
        />
      )}

      {permissionType === PermissionTypeEnum.enum.custom && isKeyIntegrationEnabled && (
        <SS.StartEndDateGroup>
          <SS.PermissionGrid>
            <DatePickerInput
              label={t('visitors:form.startDate')}
              placeholder={t('visitors:dashboard.permissionSection.selectDatePlaceholder')}
              mode="single"
              value={startOfDay}
              minDate={minDate}
              onChange={(date) => setValue('startOfDay', date, { shouldValidate: true })}
            />
            <SelectFormField
              name="timeStart"
              label={t('visitors:form.startTimeLabel')}
              placeholder={t('visitors:dashboard.permissionSection.selectTimePlaceholder')}
              icon={ClockIcon}
              options={TIME_OPTIONS}
            />
          </SS.PermissionGrid>
          <SS.PermissionGrid>
            <DatePickerInput
              label={t('visitors:form.endDate')}
              placeholder={t('visitors:dashboard.permissionSection.selectDatePlaceholder')}
              mode="single"
              value={endOfDay}
              minDate={startOfDay ?? minDate}
              onChange={(date) => setValue('endOfDay', date, { shouldValidate: true })}
            />
            <SelectFormField
              name="timeEnd"
              label={t('visitors:form.endTimeLabel')}
              placeholder={t('visitors:dashboard.permissionSection.selectTimePlaceholder')}
              icon={ClockIcon}
              options={TIME_OPTIONS}
            />
          </SS.PermissionGrid>
        </SS.StartEndDateGroup>
      )}

      {permissionType === PermissionTypeEnum.enum.custom && !isKeyIntegrationEnabled && (
        <SS.PermissionGrid3>
          <DatePickerInput
            label={t('visitors:form.dateLabel')}
            placeholder={t('visitors:dashboard.permissionSection.selectDatePlaceholder')}
            mode="single"
            value={dueDate}
            minDate={minDate}
            onChange={(date) => setValue('dueDate', date, { shouldValidate: true })}
          />
          <SelectFormField
            name="timeStart"
            label={t('visitors:form.startTimeLabel')}
            placeholder={t('visitors:dashboard.permissionSection.selectTimePlaceholder')}
            icon={ClockIcon}
            options={TIME_OPTIONS}
          />
          <SelectFormField
            name="timeEnd"
            label={t('visitors:form.endTimeLabel')}
            placeholder={t('visitors:dashboard.permissionSection.selectTimePlaceholder')}
            icon={ClockIcon}
            options={TIME_OPTIONS}
          />
        </SS.PermissionGrid3>
      )}

      {permissionType === PermissionTypeEnum.enum.daily && (
        <SS.PermissionGrid>
          <SelectFormField
            name="timeStart"
            label={t('visitors:form.startTimeLabel')}
            placeholder={t('visitors:dashboard.permissionSection.selectTimePlaceholder')}
            icon={ClockIcon}
            options={TIME_OPTIONS}
          />
          <SelectFormField
            name="timeEnd"
            label={t('visitors:form.endTimeLabel')}
            placeholder={t('visitors:dashboard.permissionSection.selectTimePlaceholder')}
            icon={ClockIcon}
            options={TIME_OPTIONS}
          />
        </SS.PermissionGrid>
      )}

      {permissionType === PermissionTypeEnum.enum.weekly && (
        <>
          <SS.FullWidthAlert
            type="info"
            message={t('visitors:dashboard.permissionSection.weeklyAlert')}
          />
          <SS.DaysList>
            {WEEKDAYS.map(({ index, label }) => (
              <SS.DayButton
                key={index}
                type="button"
                $isActive={selectedDays.includes(index)}
                onClick={() => handleDayToggle(index)}
              >
                {label}
              </SS.DayButton>
            ))}
          </SS.DaysList>
          <SS.PermissionGrid>
            <SelectFormField
              name="timeStart"
              label={t('visitors:form.startTimeLabel')}
              placeholder={t('visitors:dashboard.permissionSection.selectTimePlaceholder')}
              icon={ClockIcon}
              options={TIME_OPTIONS}
            />
            <SelectFormField
              name="timeEnd"
              label={t('visitors:form.endTimeLabel')}
              placeholder={t('visitors:dashboard.permissionSection.selectTimePlaceholder')}
              icon={ClockIcon}
              options={TIME_OPTIONS}
            />
          </SS.PermissionGrid>
        </>
      )}

      {permissionType === PermissionTypeEnum.enum.monthly && (
        <>
          <S.CalendarWrapper>
            <DatePicker
              mode="range"
              value={monthlyRange}
              minDate={minDate}
              periodOptions={[]}
              onRangeChange={(range) => {
                const [rangeStart] = range
                setValue('monthlyRange', range, { shouldValidate: true })
                if (rangeStart) setValue('dueDate', rangeStart, { shouldValidate: true })
                setValue('monthlyPermission', calculateMonthlyPattern(range), {
                  shouldValidate: true,
                })
              }}
            />
          </S.CalendarWrapper>
          <SS.FullWidthAlert
            type="info"
            message={
              monthlyPermission || t('visitors:dashboard.permissionSection.monthlyRangeHint')
            }
          />
          <SS.PermissionGrid>
            <SelectFormField
              name="timeStart"
              label={t('visitors:form.startTimeLabel')}
              placeholder={t('visitors:dashboard.permissionSection.selectTimePlaceholder')}
              icon={ClockIcon}
              options={TIME_OPTIONS}
            />
            <SelectFormField
              name="timeEnd"
              label={t('visitors:form.endTimeLabel')}
              placeholder={t('visitors:dashboard.permissionSection.selectTimePlaceholder')}
              icon={ClockIcon}
              options={TIME_OPTIONS}
            />
          </SS.PermissionGrid>
        </>
      )}
    </SS.Root>
  )
}
